/**
 * Batch Location Creation - Calculation Utilities
 * 
 * Utilidades para cálculos relacionados con la creación en lote:
 * - Total de ubicaciones
 * - Tiempo estimado
 * - Desglose por tipo
 * - Formateo de consecutivos
 * 
 * @module BatchCalculationUtils
 */

import { 
  BatchCreationConfig, 
  LocationDepth,
  ConsecutiveFormat 
} from '../models/batch-creation-config.model';

/**
 * Resultado del cálculo de ubicaciones
 */
export interface LocationCalculationResult {
  /**
   * Total de ubicaciones a crear
   */
  total: number;

  /**
   * Desglose por tipo
   */
  byType: {
    rows: number;
    racks: number;
    levels: number;
    positions: number;
  };

  /**
   * Tiempo estimado en segundos
   */
  estimatedSeconds: number;

  /**
   * Tiempo estimado formateado (ej: "2m 30s")
   */
  estimatedFormatted: string;
}

/**
 * Calcula el total de ubicaciones y desglose por tipo
 * 
 * @param config - Configuración de creación en lote
 * @returns Resultado con totales y desglose
 */
export function calculateLocations(config: BatchCreationConfig): LocationCalculationResult {
  const { depth, dimensions } = config;
  const { rows = 0, racks = 0, levels = 0, positions } = dimensions;

  let totalRows = 0;
  let totalRacks = 0;
  let totalLevels = 0;
  let totalPositions = 0;
  let total = 0;

  switch (depth) {
    case 1:
      // Depth 1: Solo posiciones bajo salón
      totalPositions = positions;
      total = positions;
      break;

    case 2:
      // Depth 2: Filas + Posiciones
      totalRows = rows;
      totalPositions = rows * positions;
      total = totalRows + totalPositions;
      break;

    case 3:
      // Depth 3: Filas + Racks + Posiciones
      totalRows = rows;
      totalRacks = rows * racks;
      totalPositions = rows * racks * positions;
      total = totalRows + totalRacks + totalPositions;
      break;

    case 4:
      // Depth 4: Filas + Racks + Niveles + Posiciones
      totalRows = rows;
      totalRacks = rows * racks;
      totalLevels = rows * racks * levels;
      totalPositions = rows * racks * levels * positions;
      total = totalRows + totalRacks + totalLevels + totalPositions;
      break;
  }

  const estimatedSeconds = estimateCreationTime(total);

  return {
    total,
    byType: {
      rows: totalRows,
      racks: totalRacks,
      levels: totalLevels,
      positions: totalPositions,
    },
    estimatedSeconds,
    estimatedFormatted: formatDuration(estimatedSeconds),
  };
}

/**
 * Estima el tiempo de creación en segundos
 * Basado en benchmark de 100 ubicaciones/segundo
 * 
 * @param totalLocations - Total de ubicaciones
 * @returns Tiempo estimado en segundos
 */
export function estimateCreationTime(totalLocations: number): number {
  const LOCATIONS_PER_SECOND = 100;
  return Math.ceil(totalLocations / LOCATIONS_PER_SECOND);
}

/**
 * Formatea una duración en segundos a texto legible
 * 
 * @param seconds - Duración en segundos
 * @returns Texto formateado (ej: "2m 30s", "1h 5m", "45s")
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    return remainingSeconds > 0 
      ? `${minutes}m ${remainingSeconds}s` 
      : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }

  return `${hours}h`;
}

/**
 * Formatea un consecutivo según el formato especificado
 * 
 * @param index - Número a formatear (1-based)
 * @param format - Formato ('numeric' o 'padded')
 * @param paddingDigits - Cantidad de dígitos para padding (default: 2)
 * @returns Consecutivo formateado
 * 
 * @example
 * formatConsecutive(5, 'numeric')      // "5"
 * formatConsecutive(5, 'padded', 2)    // "05"
 * formatConsecutive(5, 'padded', 3)    // "005"
 * formatConsecutive(100, 'padded', 2)  // "100" (no trunca)
 */
export function formatConsecutive(
  index: number,
  format: ConsecutiveFormat = 'numeric',
  paddingDigits = 2
): string {
  if (format === 'numeric') {
    return index.toString();
  }

  // Padded: agregar ceros a la izquierda
  return index.toString().padStart(paddingDigits, '0');
}

/**
 * Calcula el total por fórmula según profundidad
 * 
 * @param depth - Profundidad (1-4)
 * @param F - Filas
 * @param R - Racks
 * @param L - Niveles
 * @param P - Posiciones
 * @returns Total calculado
 */
export function calculateByFormula(
  depth: LocationDepth,
  F = 0,
  R = 0,
  L = 0,
  P = 0
): number {
  switch (depth) {
    case 1:
      return P;
    case 2:
      return F + (F * P);
    case 3:
      return F + (F * R) + (F * R * P);
    case 4:
      return F + (F * R) + (F * R * L) + (F * R * L * P);
    default:
      return 0;
  }
}

/**
 * Verifica si un total es considerado "grande"
 * (>= 500 ubicaciones)
 * 
 * @param total - Total de ubicaciones
 * @returns true si es grande
 */
export function isLargeBatch(total: number): boolean {
  return total >= 500;
}

/**
 * Verifica si un total es considerado "muy grande"
 * (>= 1000 ubicaciones)
 * 
 * @param total - Total de ubicaciones
 * @returns true si es muy grande
 */
export function isVeryLargeBatch(total: number): boolean {
  return total >= 1000;
}

/**
 * Obtiene un mensaje de advertencia si el batch es grande
 * 
 * @param total - Total de ubicaciones
 * @returns Mensaje de advertencia o null
 */
export function getBatchSizeWarning(total: number): string | null {
  if (total >= 2000) {
    return 'Batch muy grande (>= 2000). La creación puede tomar varios minutos.';
  }
  if (total >= 1000) {
    return 'Batch grande (>= 1000). Asegúrate de revisar la configuración antes de crear.';
  }
  if (total >= 500) {
    return 'Este batch creará más de 500 ubicaciones. Considera dividir en batches más pequeños.';
  }
  return null;
}

/**
 * Calcula el porcentaje que representa cada tipo
 * 
 * @param byType - Desglose por tipo
 * @param total - Total de ubicaciones
 * @returns Porcentajes por tipo
 */
export function calculateTypePercentages(
  byType: { rows: number; racks: number; levels: number; positions: number },
  total: number
): { rows: number; racks: number; levels: number; positions: number } {
  if (total === 0) {
    return { rows: 0, racks: 0, levels: 0, positions: 0 };
  }

  return {
    rows: Math.round((byType.rows / total) * 100),
    racks: Math.round((byType.racks / total) * 100),
    levels: Math.round((byType.levels / total) * 100),
    positions: Math.round((byType.positions / total) * 100),
  };
}

/**
 * Obtiene un resumen textual de la configuración
 * 
 * @param config - Configuración de batch
 * @returns Texto descriptivo
 * 
 * @example
 * "5 filas × 4 racks × 3 niveles × 10 posiciones = 670 ubicaciones"
 */
export function getConfigSummary(config: BatchCreationConfig): string {
  const { depth, dimensions } = config;
  const { rows, racks, levels, positions } = dimensions;
  const calc = calculateLocations(config);

  switch (depth) {
    case 1:
      return `${positions} posiciones = ${calc.total} ubicaciones`;
    case 2:
      return `${rows} filas × ${positions} posiciones = ${calc.total} ubicaciones`;
    case 3:
      return `${rows} filas × ${racks} racks × ${positions} posiciones = ${calc.total} ubicaciones`;
    case 4:
      return `${rows} filas × ${racks} racks × ${levels} niveles × ${positions} posiciones = ${calc.total} ubicaciones`;
    default:
      return `${calc.total} ubicaciones`;
  }
}

/**
 * Verifica si la configuración genera más de un límite específico
 * 
 * @param config - Configuración
 * @param limit - Límite máximo
 * @returns true si excede el límite
 */
export function exceedsLimit(config: BatchCreationConfig, limit: number): boolean {
  const calc = calculateLocations(config);
  return calc.total > limit;
}
